import { io, Socket } from "socket.io-client";
import { expireSession } from "./sessionExpiry";
import { BASE_URL } from "../../../api/config";
import { getClientDevice } from "../../../api/clientDevice";

let socket: Socket | null = null;
const joinedRooms = new Set<string>();

/**
 * Coda di listener registrati prima che il socket fosse creato.
 * Quando il socket viene inizializzato, tutti i listener in coda vengono applicati.
 */
const pendingListeners: Array<{ event: string; callback: (data: any) => void }> = [];

const normalizeRoomId = (ExternalActivityId: unknown) => {
    if (ExternalActivityId == null) return "";
    return String(ExternalActivityId).trim();
};

const flushJoinedRooms = () => {
    if (!socket?.connected) return;

    joinedRooms.forEach((roomId) => {
        socket?.emit("join-booking", roomId);
    });
};

/**
 * Svuota la coda dei listener pendenti registrandoli sul socket appena creato.
 */
const flushPendingListeners = () => {
    if (!socket) return;

    pendingListeners.forEach(({ event, callback }) => {
        socket?.on(event, callback);
    });
    pendingListeners.length = 0;
};

/**
 * Inizializza o riconnette il socket con le credenziali attuali
 */
export const connectSocket = async () => {
    const token = localStorage.getItem("authToken");

    // Se non c'è il token, non proviamo nemmeno a connetterci per evitare NO_AUTH
    if (!token) {
        console.warn("⚠️ [Socket] Connessione rimandata: JWT non trovato.");
        return;
    }

    // Se il socket è già connesso, non fare nulla
    if (socket?.connected) {
        console.log("📡 [Socket] Già connesso.");
        return;
    }

    // Se il socket esiste ma non è connesso, aggiorna il token e riconnetti
    // senza creare una nuova istanza (i listener dei componenti restano validi)
    if (socket) {
        console.log("🔄 [Socket] Riconnessione con token aggiornato...");
        (socket.auth as Record<string, string>).token = token;
        socket.connect();
        return;
    }

    // Prima connessione: crea l'istanza
	console.log("🔌 [Socket] Creazione socket autenticato.");
    socket = io(BASE_URL, {
        auth: {
            token: token // Questo finirà in socket.handshake.auth.token sul server
        },
		transports: ["websocket", "polling"],
    });

    socket.on("connect_error", (err) => {
        console.error("🚨 [Socket] Errore di connessione:", err.message);

        // There is no refresh credential in this application. A rejected JWT
        // requires a new login instead of calling an unavailable endpoint.
        if (err.message === "SESSION_EXPIRED" || err.message === "UNAUTHORIZED") {
            socket?.disconnect();
            expireSession();
        }
    });
    socket.on("connect", () => {
        console.log("📡 [Socket] Connessione stabilita con successo su:", BASE_URL);
        flushJoinedRooms();
    });

    socket.on("connect", () => {
        try {
            socket?.emit("client-hints", getClientDevice(navigator));
        } catch (error) {
            console.warn("[Socket] Client Hints non disponibili:", error);
        }
    });

    socket.on("disconnect", (reason) => {
        console.log("🔌 [Socket] Disconnesso:", reason);
    });

    // Applica tutti i listener registrati prima che il socket fosse creato
    flushPendingListeners();
};

// 1. Esporta l'istanza per i componenti
export const getSocket = () => socket;

// 2. Unisce l'utente a una stanza specifica
export const joinRoom = (ExternalActivityId: unknown) => {
    const normalizedBookingId = normalizeRoomId(ExternalActivityId);
    if (!normalizedBookingId) return;

    joinedRooms.add(normalizedBookingId);

    if (socket && socket.connected) {
        socket.emit("join-booking", normalizedBookingId);
    }
};

export const leaveRoom = (ExternalActivityId: unknown) => {
    const normalizedBookingId = normalizeRoomId(ExternalActivityId);
    if (!normalizedBookingId) return;

    joinedRooms.delete(normalizedBookingId);

    if (socket && socket.connected) {
        socket.emit("leave-booking", normalizedBookingId);
    }
};

// 3. Listener pulito per gli eventi
export const onSocketEvent = (event: string, callback: (data: any) => void) => {
    if (!socket) {
        // Se il socket non è ancora stato creato, accoda il listener
        // per essere registrato quando connectSocket() verrà chiamato
        pendingListeners.push({ event, callback });
        return () => {
            const idx = pendingListeners.findIndex(
                (l) => l.event === event && l.callback === callback
            );
            if (idx > -1) pendingListeners.splice(idx, 1);
            socket?.off(event, callback);
        };
    }
    socket.on(event, callback);
    return () => socket?.off(event, callback);
};

// 🚀 Primo tentativo di connessione al caricamento del file
//chiamata automatica del socketManager per stabilire la connessione quando il file viene importato
//connectSocket();

// ... (codice esistente)

/**
 * Disconnette il socket (da chiamare al Logout)
 */
export const disconnectSocket = () => {
    if (socket) {
        console.log("🔌 [Socket] Chiusura connessione manuale...");
        socket.disconnect();
        socket = null;
        joinedRooms.clear();
    }
};

/**
 * Listener specifico per il refresh globale delle prenotazioni
 * @param callback Funzione da eseguire quando arriva una nuova prenotazione
 */
export const onBookingRefresh = (callback: (data: { id: string, UserId: string, ricevutoIl: string }) => void) => {
    if (!socket) {
        // Se il socket non è pronto, riproviamo la connessione
        connectSocket();
    }
    
    // Usiamo la tua funzione onSocketEvent già esistente
    return onSocketEvent('refresh_bookings', (data) => {
        console.log("🔄 [Socket] Richiesto refresh lista prenotazioni:", data);
        callback(data);
    });
};

// 4. Gestione errori migliorata nel connectSocket
// All'interno di socket.on("connect_error", ...) aggiungi questo:
/*
    socket.on("connect_error", async (err) => {
        console.error("🚨 [Socket] Errore di connessione:", err.message);
        
        if (err.message === "SESSION_EXPIRED" || err.message === "UNAUTHORIZED") {
            console.log("🔑 [Socket] Token scaduto, tentiamo il refresh...");
            const newToken = await refreshToken(); // Usa la tua funzione importata
            if (newToken) {
                localStorage.setItem("authToken", newToken);
                connectSocket(); // Riprova con il nuovo token
            }
        }
    });
*/
